import { lazy, Suspense } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Alert,
  Card,
  Col,
  Empty,
  Flex,
  Grid,
  List,
  Progress,
  Row,
  Segmented,
  Select,
  Skeleton,
  Space,
  Statistic,
  Table,
  Tag,
  theme,
  Tooltip,
  Typography,
} from 'antd';
import { useNavigate } from 'react-router';
import { useEstadoDeLista } from '@titularis/ui';
import type { DiaDeProgresso, ItemDaFila, ProgressoDeProva } from '@titularis/api-client';
import { api } from '../api';
import { PrevisaoDeConclusao } from './progresso/PrevisaoDeConclusao';
import { blocoDaConsulta, blocoDoRitmo } from './progresso/densidadeDoRitmo';
import { diaDeHoje, linhasPorDia } from './progresso/serieDoRitmo';
import {
  ESTADO_COMENTARIO_COR,
  ESTADO_COMENTARIO_LABEL,
  MODULO_LABEL,
} from '../dominio';
/*
 * O gráfico chega depois do resto da tela.
 *
 * A biblioteca de desenho custa uns 107kB comprimidos, e esta é a tela inicial
 * do backoffice: pagá-los no primeiro carregamento atrasaria os números do topo
 * - que são o que se olha primeiro - por causa de um desenho que está abaixo da
 * dobra. Ele entra no lugar assim que chega.
 */
/** Quando o time começou a escrever para valer: a origem padrão da medição. */
const INICIO_DA_MEDICAO = '2026-08-08';

const GraficoDeRitmo = lazy(() =>
  import('./progresso/GraficoDeRitmo').then((modulo) => ({ default: modulo.GraficoDeRitmo })),
);

/**
 * Progresso do preenchimento dos comentários (F9).
 *
 * É a tela inicial da administração, e é de propósito: o limitador declarado da
 * empresa é a escrita dos comentários, e o que a pessoa vê ao abrir o backoffice
 * deve ser onde o trabalho está.
 *
 * **Duas medidas, porque são perguntas diferentes.** Questão comentada é marco,
 * e marco anda em degraus - uma objetiva de cinco alternativas leva horas e fica
 * em zero até fechar. Peça (cada item comentado, cada aba preenchida) é a
 * unidade pequena, e é ela que mostra um dia de trabalho.
 *
 * **A fila do quase pronto é a parte acionável.** O resto informa; ela diz o que
 * fazer na próxima hora, ordenada pelo que fecha mais barato.
 *
 * **O gráfico entrou quando houve história para desenhar.** Estoque e fila são
 * o agora, e barra e tabela bastam; ritmo é o período, e período pede forma.
 * Ver `progresso/GraficoDeRitmo.tsx` para as escolhas do desenho - sobretudo
 * por que as duas medidas dividem uma escala só.
 *
 * **No celular as tabelas viram listas.** Tabela de três a cinco colunas em
 * 390px não encolhe, ela quebra: o nome da prova descia seis linhas e a coluna
 * de número sobrava sozinha. Rolagem horizontal resolveria o desenho e não a
 * leitura - ninguém rola de lado para saber o que fazer a seguir. Cada linha
 * vira um item empilhado, com o número em destaque e o resto abaixo.
 */

/**
 * Janelas do ritmo: uma semana, duas, três, um mês, um trimestre.
 *
 * As três curtas existem porque é nelas que se acompanha o trabalho da semana,
 * e porque de sete para trinta dias o salto era grande demais - quem queria ver
 * "o que andou desde a quinzena" só tinha o mês, com a quinzena diluída dentro
 * dele. O gráfico também rende mais nelas: a coluna fica menor que o dia e o
 * total cabe escrito em cima dela (ver `progresso/densidadeDoRitmo.ts`).
 */
const JANELAS = [7, 14, 21, 30, 90];

/**
 * A quinzena é o padrão.
 *
 * Um mês era largo demais para a pergunta que se faz ao abrir o painel, que é
 * "como estamos indo": a semana corrente fica diluída no meio do mês passado. A
 * quinzena mostra o trabalho recente e ainda alcança a semana retrasada, que é
 * com o que se compara.
 */
const JANELA_PADRAO = 14;

const porcentagem = (feito: number, total: number) =>
  total === 0 ? 0 : Math.round((feito / total) * 100);

export function ProgressoPage() {
  const { token } = theme.useToken();
  const navigate = useNavigate();
  const telas = Grid.useBreakpoint();
  const compacto = !telas.md;
  const { ler, filtrar } = useEstadoDeLista();

  // Todo o estado vive na URL, como nas demais listagens: o painel filtrado é
  // endereço, e dá para mandar o link de "o que falta em Cartórios" para alguém.
  const scopeId = ler.texto('concurso');
  const examId = ler.texto('prova');
  const disciplineId = ler.texto('disciplina');
  const responsibleId = ler.texto('responsavel');
  const dias = Number(ler.texto('dias')) || JANELA_PADRAO;
  /*
   * O tamanho da coluna sai da janela e da tela, e não de um controle: é
   * decisão de desenho, não de quem lê. Quem agrupa é a API - o dia que ela
   * devolvesse inteiro não teria como ser partido aqui -, mas a consulta pede
   * o bloco mais fino de todas as telas e o desenho recolhe o que sobra, para
   * a largura do navegador não virar ida ao servidor.
   */
  const bloco = blocoDoRitmo(dias, compacto);
  /*
   * O período da previsão, também na URL: "quando terminamos no ritmo de
   * agosto" é pergunta que se manda por link, como o resto desta tela.
   */
  const de = ler.texto('de') || INICIO_DA_MEDICAO;
  const ate = ler.texto('ate') || diaDeHoje();

  const filtros = { scopeId, examId, disciplineId, responsibleId };

  const concursos = useQuery({
    queryKey: ['scopes', 'contest'],
    queryFn: () => api.listScopes({ kind: 'contest' }),
  });
  const provas = useQuery({
    queryKey: ['exams', 'progresso'],
    // 100 é o teto do contrato. Pedindo mais, a rota recusa com 422 e o
    // seletor fica vazio sem dizer por quê - foi assim que ele nasceu quebrado.
    queryFn: () => api.listExams({ pageSize: 100 }),
  });
  const disciplinas = useQuery({
    queryKey: ['disciplines', scopeId ?? 'todas'],
    queryFn: () => api.listDisciplines(scopeId ? { scopeId } : {}),
  });
  const responsaveis = useQuery({
    queryKey: ['responsaveis'],
    queryFn: () => api.listQuestionResponsibles(),
  });

  const panorama = useQuery({
    queryKey: ['progresso', filtros],
    queryFn: () => api.progresso(filtros),
  });
  const fila = useQuery({
    queryKey: ['progresso-fila', filtros],
    queryFn: () => api.filaDoProgresso({ ...filtros, limite: 30 }),
  });
  const ritmo = useQuery({
    queryKey: ['progresso-ritmo', filtros, dias],
    queryFn: () => api.ritmoDoProgresso({ ...filtros, dias, bloco: blocoDaConsulta(dias) }),
  });
  /*
   * A tabela e a lista do celular são por dia, e o gráfico é por bloco: os
   * blocos do dia voltam a ser o dia aqui, sem uma segunda ida ao servidor
   * para buscar os mesmos números.
   */
  const ritmoPorDia = linhasPorDia(ritmo.data ?? []);

  const resumo = panorama.data?.resumo;
  /*
   * A terceira casa do estoque, e a única derivada: as três se excluem e somam
   * o total, então ela é o que sobra. Calculada aqui em vez de vir da API pelo
   * mesmo motivo de sempre - um número a mais no contrato é um número a mais
   * para discordar do resto.
   */
  const aEscrever = resumo ? resumo.total - resumo.comentadas - resumo.aRevisar : 0;

  const colunasDaProva = [
    {
      title: 'Prova',
      dataIndex: 'examName',
      render: (nome: string, linha: ProgressoDeProva) => (
        <Space orientation="vertical" size={0}>
          <Typography.Text strong>{nome}</Typography.Text>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {linha.scopeName} · {MODULO_LABEL[linha.module as keyof typeof MODULO_LABEL] ?? linha.module}
          </Typography.Text>
        </Space>
      ),
    },
    {
      // A mesma barra de duas faixas do topo: a prova que a máquina fechou não
      // pode parecer pronta na lista que existe para dizer onde está o serviço.
      title: 'Questões comentadas',
      width: 220,
      render: (_: unknown, linha: ProgressoDeProva) => (
        <Space orientation="vertical" size={2} style={{ width: '100%' }}>
          <Progress
            percent={porcentagem(linha.comentadas + linha.aRevisar, linha.total)}
            success={{ percent: porcentagem(linha.comentadas, linha.total) }}
            format={() => `${porcentagem(linha.comentadas, linha.total)}%`}
            size="small"
            status={linha.comentadas === linha.total ? 'success' : 'active'}
          />
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {linha.comentadas} de {linha.total}
            {linha.aRevisar > 0 ? ` · ${linha.aRevisar} a revisar` : ''}
          </Typography.Text>
        </Space>
      ),
    },
    {
      title: (
        <Tooltip title="Cada item comentado e cada aba preenchida. É a medida que mostra um dia de trabalho.">
          Peças
        </Tooltip>
      ),
      width: 140,
      align: 'right' as const,
      render: (_: unknown, linha: ProgressoDeProva) => (
        <Space orientation="vertical" size={0} style={{ alignItems: 'flex-end' }}>
          <Typography.Text>
            {linha.pecasFeitas} / {linha.pecasTotais}
          </Typography.Text>
          {linha.pecasPorRevisar > 0 ? (
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              +{linha.pecasPorRevisar} a revisar
            </Typography.Text>
          ) : null}
        </Space>
      ),
    },
    {
      title: 'Falta',
      width: 100,
      align: 'right' as const,
      render: (_: unknown, linha: ProgressoDeProva) => {
        // Contra o que **gente** escreveu: a sugestão encurta o trabalho, não o
        // elimina, e ninguém mediu ainda quanto ela encurta.
        const faltam = linha.pecasTotais - linha.pecasFeitas;
        return faltam === 0 ? (
          <Tag color="green">Pronta</Tag>
        ) : (
          <Typography.Text type="secondary">{faltam} peça(s)</Typography.Text>
        );
      },
    },
  ];

  const colunasDaFila = [
    {
      title: 'Questão',
      render: (_: unknown, linha: ItemDaFila) => (
        <Space orientation="vertical" size={0}>
          <Typography.Text>
            {linha.position ? `Questão ${linha.position}` : 'Sem posição'}
          </Typography.Text>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {linha.examName}
          </Typography.Text>
        </Space>
      ),
    },
    {
      title: 'O que falta',
      width: 180,
      render: (_: unknown, linha: ItemDaFila) => (
        <Tag color={ESTADO_COMENTARIO_COR[linha.estado]}>
          {ESTADO_COMENTARIO_LABEL[linha.estado]}
        </Tag>
      ),
    },
    {
      title: 'Peças',
      width: 90,
      align: 'right' as const,
      dataIndex: 'faltam',
    },
  ];

  /*
   * As versões de celular.
   *
   * O número que importa fica à direita, isolado; o texto longo fica à esquerda
   * e pode truncar. É o inverso da tabela, onde o texto empurrava o número para
   * fora e quebrava as duas coisas.
   */
  const filaNoCelular = (
    <List
      size="small"
      dataSource={fila.data}
      renderItem={(item: ItemDaFila) => (
        <List.Item
          style={{ cursor: 'pointer', paddingInline: 0 }}
          onClick={() => void navigate(`/questoes/${item.questionId}`)}
          extra={
            <Flex vertical align="flex-end" gap={2} style={{ minWidth: 62 }}>
              <Typography.Text strong style={{ fontSize: 18, lineHeight: 1 }}>
                {item.faltam}
              </Typography.Text>
              <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                peça(s)
              </Typography.Text>
            </Flex>
          }
        >
          <List.Item.Meta
            title={
              <Space size={6} wrap>
                <Typography.Text strong>
                  {item.position ? `Questão ${item.position}` : 'Sem posição'}
                </Typography.Text>
                <Tag color={ESTADO_COMENTARIO_COR[item.estado]} style={{ marginInlineEnd: 0 }}>
                  {ESTADO_COMENTARIO_LABEL[item.estado]}
                </Tag>
              </Space>
            }
            description={
              // Truncado, e não quebrado em seis linhas: o nome da prova é
              // contexto, e o aviso do que fazer é o título acima.
              <Typography.Text type="secondary" ellipsis style={{ fontSize: 12 }}>
                {item.examName}
              </Typography.Text>
            }
          />
        </List.Item>
      )}
    />
  );

  const provasNoCelular = (
    <List
      size="small"
      dataSource={panorama.data?.porProva}
      renderItem={(item: ProgressoDeProva) => (
        <List.Item style={{ paddingInline: 0 }}>
          <Space orientation="vertical" size={4} style={{ width: '100%' }}>
            <Typography.Text strong ellipsis>
              {item.examName}
            </Typography.Text>
            <Progress
              percent={porcentagem(item.comentadas + item.aRevisar, item.total)}
              success={{ percent: porcentagem(item.comentadas, item.total) }}
              format={() => `${porcentagem(item.comentadas, item.total)}%`}
              size="small"
              status={item.comentadas === item.total ? 'success' : 'active'}
            />
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {item.comentadas} de {item.total} comentadas · {item.pecasFeitas}/
              {item.pecasTotais} peças
              {item.aRevisar > 0 ? ` · ${item.aRevisar} a revisar` : ''}
            </Typography.Text>
          </Space>
        </List.Item>
      )}
    />
  );

  const ritmoNoCelular = (
    <List
      size="small"
      dataSource={ritmoPorDia}
      renderItem={(item: DiaDeProgresso) => (
        <List.Item style={{ paddingInline: 0 }}>
          <Space orientation="vertical" size={2} style={{ width: '100%' }}>
            <Space size={8}>
              <Typography.Text strong>{item.dia}</Typography.Text>
              <Typography.Text type="secondary">
                {item.autorNome ?? 'Conta excluída'}
              </Typography.Text>
            </Space>
            <Typography.Text style={{ fontSize: 12 }}>
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                {item.versoes} versão(ões) ·{' '}
              </Typography.Text>
              {item.pecas} escrita(s)
              {item.pecasValidadas > 0 ? ` · ${item.pecasValidadas} validada(s)` : ''}
              {item.fechadas > 0 ? ` · ${item.fechadas} fechada(s)` : ''}
            </Typography.Text>
          </Space>
        </List.Item>
      )}
    />
  );

  const colunasDoRitmo = [
    { title: 'Dia', dataIndex: 'dia', width: 130 },
    {
      title: 'Quem',
      render: (_: unknown, linha: DiaDeProgresso) =>
        linha.autorNome ?? <Typography.Text type="secondary">Conta excluída</Typography.Text>,
    },
    {
      /*
       * Atividade, e não entrega - por isso vem sem barra e sem meta, em tom
       * secundário. Corrigir uma vírgula e reescrever um comentário inteiro
       * contam igual aqui; ler esta coluna como produtividade seria erro.
       */
      title: (
        <Tooltip title="Quantas versões a pessoa salvou. Mede atividade, não entrega: um dia de cadastro ou de correção de enunciado aparece aqui e não nas peças.">
          Versões salvas
        </Tooltip>
      ),
      dataIndex: 'versoes',
      width: 140,
      align: 'right' as const,
      render: (valor: number) => <Typography.Text type="secondary">{valor}</Typography.Text>,
    },
    {
      title: (
        <Tooltip title="Peças que a pessoa redigiu: item comentado ou aba preenchida por ela.">
          Peças escritas
        </Tooltip>
      ),
      dataIndex: 'pecas',
      width: 130,
      align: 'right' as const,
    },
    {
      /*
       * A coluna que faltava. Sem ela, o dia inteiro de quem revisa sugestão da
       * máquina saía zerado: a peça já estava contada e a régua já estava
       * fechada, então nem o delta nem a transição disparavam.
       */
      title: (
        <Tooltip title="Peças que a máquina escreveu e a pessoa conferiu e assinou, salvando a questão ou publicando a sugestão.">
          Peças validadas
        </Tooltip>
      ),
      dataIndex: 'pecasValidadas',
      width: 140,
      align: 'right' as const,
      render: (valor: number) =>
        valor > 0 ? valor : <Typography.Text type="secondary">0</Typography.Text>,
    },
    { title: 'Questões fechadas', dataIndex: 'fechadas', width: 150, align: 'right' as const },
  ];

  return (
    <Space orientation="vertical" size="large" style={{ width: '100%' }}>
      <div>
        {/* Título menor no celular: nível 3 come duas linhas de 390px. */}
        <Typography.Title level={compacto ? 4 : 3} style={{ marginBottom: 4 }}>
          Progresso dos comentários
        </Typography.Title>
        <Typography.Text type="secondary">
          Quanto do catálogo já tem comentário, em que ritmo e o que fecha mais barato.
        </Typography.Text>
      </div>

      {/*
        No celular cada filtro ocupa a linha inteira. Lado a lado com 390px eles
        ficariam com uns 80px cada, e "1º Exame Nacional dos Cartórios" numa
        caixa desse tamanho não é escolha, é adivinhação.
      */}
      <Card size="small">
        <Flex gap={12} wrap vertical={compacto}>
          <Select
            allowClear
            placeholder="Concurso"
            style={{ width: compacto ? '100%' : 190 }}
            value={scopeId || undefined}
            loading={concursos.isPending}
            onChange={(valor) => filtrar({ concurso: valor, prova: undefined, disciplina: undefined })}
            options={(concursos.data ?? []).map((escopo) => ({
              value: escopo.id,
              label: escopo.name,
            }))}
          />
          <Select
            allowClear
            showSearch
            optionFilterProp="label"
            placeholder="Prova"
            style={{ width: compacto ? '100%' : 260 }}
            value={examId || undefined}
            loading={provas.isPending}
            onChange={(valor) => filtrar({ prova: valor })}
            options={(provas.data?.items ?? []).map((prova) => ({
              value: prova.id,
              label: prova.name,
            }))}
          />
          <Select
            allowClear
            showSearch
            optionFilterProp="label"
            placeholder="Disciplina"
            style={{ width: compacto ? '100%' : 220 }}
            value={disciplineId || undefined}
            loading={disciplinas.isPending}
            onChange={(valor) => filtrar({ disciplina: valor })}
            options={(disciplinas.data ?? []).map((item) => ({
              value: item.id,
              // Sem concurso escolhido, o mesmo nome existe em mais de um
              // escopo: o rótulo desambigua em vez de deixar escolher no escuro.
              label: scopeId ? item.name : `${item.name} · ${item.scopeName}`,
            }))}
          />
          <Select
            allowClear
            placeholder="Responsável"
            style={{ width: compacto ? '100%' : 200 }}
            value={responsibleId || undefined}
            loading={responsaveis.isPending}
            onChange={(valor) => filtrar({ responsavel: valor })}
            options={(responsaveis.data?.items ?? []).map((pessoa) => ({
              value: pessoa.id,
              label: pessoa.name ?? 'Conta excluída',
            }))}
          />
        </Flex>
      </Card>

      {/*
        Falha de filtro vira aviso, e não seletor vazio.

        Quando a listagem de provas caiu, o seletor simplesmente não tinha
        opção - indistinguível de "não há prova cadastrada". Lista vazia por
        erro é a pior forma de falhar: não parece falha.
      */}
      {[panorama.error, provas.error, concursos.error, disciplinas.error, responsaveis.error]
        .filter(Boolean)
        .slice(0, 1)
        .map((erro) => (
          <Alert
            key="erro"
            type="error"
            showIcon
            title="Não foi possível carregar tudo desta tela"
            description={(erro as Error).message}
          />
        ))}

      {panorama.isPending || !resumo ? (
        <Skeleton active paragraph={{ rows: 4 }} />
      ) : (
        /*
          Uma linha só, que quebra sozinha: três cartões de 8 colunas e dois de
          12 caem em duas fileiras no desktop e empilham no celular. Duas `Row`
          irmãs ficariam coladas, porque o `Space` de fora conta cada uma como
          um filho e o espaçamento vertical do `gutter` não atravessa.
        */
        <Row gutter={[16, 16]}>
          <Col xs={24} md={8}>
            <Card size="small" style={{ height: '100%' }}>
              <Statistic
                title="Comentadas"
                value={resumo.comentadas}
                suffix={`/ ${resumo.total}`}
              />
              {/*
                Duas faixas numa barra só. A verde é o que gente assinou; a que
                vem depois dela é o que a máquina fechou e ninguém conferiu.
                Uma barra por cartão diria o mesmo em dois lugares e deixaria a
                relação entre os dois números para quem olha descobrir.

                O rótulo mostra a **porcentagem humana**, e não a soma: quem lê
                "83%" no canto da barra lê a barra inteira, e a soma é
                justamente o número que este cartão existe para não prometer.
              */}
              <Tooltip
                title={
                  resumo.aRevisar > 0
                    ? `A barra vai até ${porcentagem(resumo.comentadas + resumo.aRevisar, resumo.total)}%, contando ${resumo.aRevisar} questão(ões) que a máquina fechou e ninguém revisou. A parte verde é o que uma pessoa assina.`
                    : undefined
                }
              >
                <Progress
                  percent={porcentagem(resumo.comentadas + resumo.aRevisar, resumo.total)}
                  success={{ percent: porcentagem(resumo.comentadas, resumo.total) }}
                  format={() => `${porcentagem(resumo.comentadas, resumo.total)}%`}
                  status={resumo.comentadas === resumo.total ? 'success' : 'active'}
                />
              </Tooltip>
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                Escritas ou revisadas por uma pessoa. É a linha de chegada.
              </Typography.Text>
            </Card>
          </Col>
          <Col xs={24} md={8}>
            <Card size="small" style={{ height: '100%' }}>
              {/*
                O cartão que o Comentário Assistido tornou necessário. Sem ele,
                a máquina escrevendo à noite empurrava "Comentadas" para cima
                sozinha, e o painel dizia pronto sobre texto que ninguém leu.
              */}
              <Statistic
                title="A revisar"
                value={resumo.aRevisar}
                valueStyle={{ color: resumo.aRevisar > 0 ? token.colorWarning : undefined }}
              />
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                O Comentário Assistido fechou a régua e ninguém conferiu ainda. Não é pronta.
              </Typography.Text>
            </Card>
          </Col>
          <Col xs={24} md={8}>
            <Card size="small" style={{ height: '100%' }}>
              <Statistic title="A escrever" value={aEscrever} />
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                Nem a máquina fechou. As começadas estão na fila abaixo.
              </Typography.Text>
            </Card>
          </Col>
          <Col xs={24} md={12}>
            <Card size="small" style={{ height: '100%' }}>
              <Statistic
                title="Peças escritas"
                value={resumo.pecasFeitas}
                suffix={`/ ${resumo.pecasTotais}`}
              />
              <Progress
                percent={porcentagem(
                  resumo.pecasFeitas + resumo.pecasPorRevisar,
                  resumo.pecasTotais,
                )}
                success={{ percent: porcentagem(resumo.pecasFeitas, resumo.pecasTotais) }}
                format={() => `${porcentagem(resumo.pecasFeitas, resumo.pecasTotais)}%`}
              />
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                Cada item comentado e cada aba preenchida.
                {resumo.pecasPorRevisar > 0
                  ? ` Mais ${resumo.pecasPorRevisar} escritas pela máquina, a revisar.`
                  : ''}
              </Typography.Text>
            </Card>
          </Col>
          <Col xs={24} md={12}>
            <Card size="small" style={{ height: '100%' }}>
              <Statistic
                title="Sem responsável"
                value={resumo.naoAssumidas}
                valueStyle={{ color: resumo.naoAssumidas > 0 ? token.colorWarning : undefined }}
              />
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                Questões cuja última versão veio da pipeline e ninguém abriu - inclusive as que só
                têm o enunciado transcrito, sem comentário nenhum. É mais largo que "A revisar".
              </Typography.Text>
            </Card>
          </Col>
        </Row>
      )}

      {/*
        Logo depois dos indicadores: a leitura da tela vira "quanto já foi,
        quando termina, o que fazer agora". Estava depois do Ritmo, que é de
        onde ela sai, mas ninguém rola até o fim da página para saber a data -
        e a fila logo abaixo continua sendo a parte acionável.
      */}
      {resumo ? (
        <PrevisaoDeConclusao
          restantes={Math.max(0, resumo.pecasTotais - resumo.pecasFeitas)}
          comSugestao={resumo.pecasPorRevisar}
          filtros={filtros}
          de={de}
          ate={ate}
          onMudarPeriodo={(inicio, fim) => filtrar({ de: inicio, ate: fim })}
          compacto={compacto}
        />
      ) : null}

      <Card
        size="small"
        title="A fazer primeiro"
        /*
         * A explicação vira legenda embaixo no celular. Como `extra`, ela
         * disputava a linha do título e ocupava duas linhas sozinha - a frase
         * ficava maior que o cartão que ela explica.
         */
        extra={
          compacto ? null : (
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              Começadas e não fechadas, do que custa menos ao que custa mais
            </Typography.Text>
          )
        }
      >
        {compacto ? (
          <Typography.Paragraph type="secondary" style={{ fontSize: 12, marginBottom: 8 }}>
            Começadas e não fechadas, do que custa menos ao que custa mais.
          </Typography.Paragraph>
        ) : null}

        {fila.isPending ? (
          <Skeleton active paragraph={{ rows: 4 }} />
        ) : fila.data?.length === 0 ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="Nada pela metade: ou está comentada, ou nem começou."
          />
        ) : compacto ? (
          filaNoCelular
        ) : (
          <Table
            size="small"
            rowKey="questionId"
            dataSource={fila.data}
            columns={colunasDaFila}
            pagination={false}
            onRow={(linha) => ({
              style: { cursor: 'pointer' },
              onClick: () => void navigate(`/questoes/${linha.questionId}`),
            })}
          />
        )}
      </Card>

      <Card size="small" title="Por prova">
        {panorama.isPending ? (
          <Skeleton active paragraph={{ rows: 4 }} />
        ) : panorama.data?.porProva.length === 0 ? (
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Nenhuma prova com questões." />
        ) : compacto ? (
          provasNoCelular
        ) : (
          <Table
            size="small"
            rowKey="examId"
            dataSource={panorama.data?.porProva}
            columns={colunasDaProva}
            pagination={false}
          />
        )}
      </Card>

      <Card
        size="small"
        title="Ritmo"
        /*
          As cinco janelas à vista no desktop, e não escondidas atrás de um
          clique.

          O `Select` guardava a lista: quem não sabia que existiam duas e três
          semanas não tinha por que abrir a caixa para descobrir. Com cinco
          opções curtas o `Segmented` mostra a régua inteira e troca de janela
          num toque - é o controle do Antd para alternar entre vistas do mesmo
          dado, que é exatamente o que estas opções são.

          No celular ele continua `Select`: cinco segmentos na linha do título
          ficariam com uns 55px cada, e a janela escolhida deixaria de se ler.
        */
        extra={
          compacto ? (
            <Select
              size="small"
              // No celular a janela ocupa o que sobra da linha do título, em
              // vez de espremer em 150px.
              style={{ width: 132 }}
              value={dias}
              onChange={(valor) => filtrar({ dias: valor })}
              options={JANELAS.map((valor) => ({ value: valor, label: `${valor} dias` }))}
            />
          ) : (
            <Segmented<number>
              size="small"
              value={dias}
              onChange={(valor) => filtrar({ dias: valor })}
              options={JANELAS.map((valor) => ({ value: valor, label: `${valor} dias` }))}
            />
          )
        }
      >
        {ritmo.isPending ? (
          <Skeleton active paragraph={{ rows: 3 }} />
        ) : ritmo.data?.length === 0 ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="Ninguém salvou questão nesta janela."
          />
        ) : (
          <Space orientation="vertical" size="middle" style={{ width: '100%' }}>
            {/*
              O gráfico vem antes da tabela porque responde outra pergunta.
              Ele mostra a forma do período - onde acelerou, onde parou - e a
              tabela mostra o dia e quem fez. Quem abre o painel quer a forma
              primeiro; o detalhe é para depois de achar o dia estranho.
            */}
            <Suspense fallback={<Skeleton active paragraph={{ rows: 3 }} />}>
              <GraficoDeRitmo
                dados={ritmo.data ?? []}
                dias={dias}
                bloco={bloco}
                compacto={compacto}
              />
            </Suspense>

            {compacto ? (
              ritmoNoCelular
            ) : (
              <Table
                size="small"
                rowKey={(linha) => `${linha.dia}-${linha.autorId ?? 'sem-autor'}`}
                dataSource={ritmoPorDia}
                columns={colunasDoRitmo}
                pagination={false}
              />
            )}
          </Space>
        )}
      </Card>
    </Space>
  );
}
